// Stamps role/tabindex/state onto non-native controls and forwards Space/Enter to .click() for the
// existing jQuery handlers. Delegated and mirrored by MutationObserver, so DOM swaps survive.
(function () {
  'use strict';

  var TILE_TITLE = '.oa-prdct20-fashion__title, .oa-item__title';
  // Where the walk for that title stops: an ancestor holding more than one toggle is the grid,
  // not the tile, and its first title belongs to some other product.
  var TILE_MARK = '.oa-quick-add__toggle';

  // stateClass: the class the theme toggles; stateAttr: its ARIA meaning; label: a key into
  // window.oaA11yLabels. A group with no role only mirrors state.
  var GROUPS = [
    { selector: '.product-sizes .oa-product-sizes--item', role: 'radio', group: 'radiogroup',
      stateClass: 'active', stateAttr: 'aria-checked', disabledClass: 'disabled' },
    { selector: '.product-sizes .oa-product-sizes--item-size', role: 'radio', group: 'radiogroup',
      stateClass: 'active', stateAttr: 'aria-checked', disabledClass: 'disabled' },
    { selector: '.product-logos .oa-product-logos--item', role: 'radio', group: 'radiogroup',
      stateClass: 'active', stateAttr: 'aria-checked', disabledClass: 'disabled' },
    // data-a11y="disclosure" marks the one that slideToggles a panel; the rest open the drawer.
    // labelFrom: the toggles carry no text, so a grid reads as 75 identical buttons without it.
    { selector: '.oa-quick-add__toggle[data-a11y="disclosure"]', role: 'button',
      label: 'quickAddToggle', labelFrom: TILE_TITLE,
      stateClass: 'oa-quick-add__toggle--active', stateAttr: 'aria-expanded' },
    { selector: '.oa-quick-add__toggle', role: 'button', label: 'quickAdd', labelFrom: TILE_TITLE },
    // Colour swatches inside the desktop quick-add panel: <div>s with a jQuery click and no
    // text of their own. The current colour is one too, marked aria-current in the markup.
    { selector: '.oa-quick-add__color .quickaddsidebar', role: 'button',
      label: 'colour', labelAttr: 'data-color' },
    // Nosto's own recommendation markup, painted from a template held in Nosto rather than in
    // this repo, so its "Load more" <div> can only be made a control from out here.
    { selector: '.nosto-btn', role: 'button' },
    // Wishlist King mounts an <a> with no href, so it is neither focusable nor announced.
    // stamp() keeps the app's aria-label; aria-pressed follows the app's own `wk-selected`.
    { selector: 'a.btn-wishlist.wk-component', role: 'button', keepText: true,
      stateClass: 'wk-selected', stateAttr: 'aria-pressed' },
    // "Read more" on the landing pages is an <a> with an onclick and no href. The tag cannot
    // change without the `a.read-more-btn` rules in twenty files losing their element.
    { selector: 'a.read-more-btn', role: 'button', keepText: true },
    // The older carousels' arrows: <a href="#"> holding only a glyph the `hide` group marks aria-hidden.
    // disabledClass is the one each slider's Swiper config toggles; SWIPER_NAV covers Enter.
    { selector: '.oa-product-slider__nav-prev', role: 'button', label: 'prevSlide',
      disabledClass: 'oa-product-slider__nav--disabled' },
    { selector: '.oa-product-slider__nav-next', role: 'button', label: 'nextSlide',
      disabledClass: 'oa-product-slider__nav--disabled' },
    { selector: '.oa-hdr19-bottom__slider__nav-prev', role: 'button', label: 'prevSlide',
      disabledClass: 'oa-hdr19-bottom__slider__nav--disabled' },
    { selector: '.oa-hdr19-bottom__slider__nav-next', role: 'button', label: 'nextSlide',
      disabledClass: 'oa-hdr19-bottom__slider__nav--disabled' },
    // Size guide openers: <a href="#"> that Bootstrap turns into a modal trigger off the
    // data-attributes, so the tag is free to stay while only the semantics change.
    { selector: 'a[data-toggle="modal"][href="#"]', role: 'button', keepText: true },
    // The PDP quantity stepper: buttons holding only a glyph.
    { selector: '.oa-qty-input .btn-dec', label: 'qtyDecrease' },
    { selector: '.oa-qty-input .btn-inc', label: 'qtyIncrease' },
    // The mobile menu's panel tabs. Real buttons with text, but nothing said which panel was
    // open; header.js.liquid moves `is-active` to exactly one of them on every click.
    { selector: '.oa-header__collapse-tab', stateClass: 'is-active', stateAttr: 'aria-expanded' },
    // The hamburger reported no state at all. oaHeaderDownnav() in header.js.liquid puts
    // --toggled on the button itself, so every close path is covered, not only the click.
    { selector: '#headertoggle', stateClass: 'oa-hdr19-top__toggle--toggled',
      stateAttr: 'aria-expanded' },
    // The desktop megamenu opens on focus, so nothing told a screen reader a menu had appeared.
    // header.js.liquid adds --active only while that link's flyout is open.
    { selector: '.oa-hdr19-main__left__link[data-href^="#flyout"]',
      stateClass: 'oa-hdr19-main__left__link--active', stateAttr: 'aria-expanded' },
    // PDP gallery images open the zoom lightbox; selectors must match what product-single.layout.js
    // binds. numberIn appends the position, or every view gets the same name.
    { selector: '.oa-fashion__product-image-container .oa-picture__img, ' +
        '.oa-product-image-slider .oa-picture__img[data-zoom-src]',
      role: 'button', label: 'zoomImage', labelAttr: 'alt',
      numberIn: '.oa-fashion__product-image-container, .oa-product-image-slider' },
    // checkbox, not radio, for every group: some are multi-select and some are not, but each
    // label toggles independently, which is what checkbox describes.
    { selector: '.oa-shop-filter__checkbox', role: 'checkbox',
      stateClass: 'checked', stateAttr: 'aria-checked' },
    { selector: '.oa-shop-filter__sub-heading', role: 'button',
      stateClass: 'active', stateAttr: 'aria-expanded' },
    // Version-A desktop filter header, a bare <div onclick>. No stateClass: the state is a dataset
    // flag, so toggleFilterPLPVerA() writes aria-expanded itself.
    { selector: '.oa-shop-filter__title-plp', role: 'button' },
    // The PLP's category chip row. Single-select, but not role="radio": arrowing between radios
    // selects as it goes, and each selection here re-filters the grid and scrolls the page to top.
    { selector: '.oa-shop-filter__checkbox-category-main', role: 'button',
      stateClass: 'checked', stateAttr: 'aria-current' },
    // The filter panel's primary CTA, and on desktop the only way to submit. keepText: its
    // result count is rewritten as text no observer here sees, so a snapshot would go stale.
    { selector: '.save-filter-button', role: 'button', keepText: true },
    // The desktop sort disclosure. clickFilter() toggles `active` on the button itself, exactly
    // like the sub-headings above, so only the state is missing — it is already a <button>.
    { selector: '.btn-trigger-filter-desktop[data-a11y="disclosure"]',
      stateClass: 'active', stateAttr: 'aria-expanded' },
    // Grid density toggles: real buttons, named in the markup, but nothing said which was on.
    { selector: '.grid-box-button', stateClass: 'grid-box-button--active', stateAttr: 'aria-pressed' },
    // The AI assistant's two close affordances are bare <div onclick>: no role, no tab stop and
    // no keyboard. Their names are in the markup, so only those three are missing here.
    { selector: '.zowie-closing, .zowie-closing--info-close', role: 'button' },
    // The bra guide's coverage switch — same shape, and the mobile dropdown beside it already
    // announces its selection, so only the button row was silent.
    { selector: '.coverage-btn', stateClass: 'btn-active', stateAttr: 'aria-pressed' },
    // Disclosure, not a tab set: each panel slideToggles independently, so several can be
    // open at once and role="tab" would promise arrow-key navigation that does not exist.
    { selector: '.desktop-tab-headline[data-tab]', role: 'button',
      stateClass: 'active', stateAttr: 'aria-expanded' },
    // The loyalty FAQ's accordion titles. `active` sits on the item, so yotpo-faq writes aria-expanded.
    { selector: '.faqs-accordion-title', role: 'button', keepText: true },
    // The help centre's Vue-painted <div>s with a click and nothing else: "Show all N Articles", search
    // hits, recently viewed questions and the mobile question picker. keepText: Vue rewrites the counts.
    // Classes, not #vue-faq: jsdom misses an id above the root of a scoped rescan, so tests could not see it.
    { selector: '.oa_show-more, .resultsWrap .result .h4, .lastWrap .h5, .oa_current-section .dropdown-item',
      role: 'button', keepText: true },
    // Its mobile section accordions. accordionToggle() moves `open` onto the header it toggles.
    { selector: '.mobileWrap .flexWrap', role: 'button', keepText: true,
      stateClass: 'open', stateAttr: 'aria-expanded' },
    // The drawer's swatches are <a> with an onclick and no href, so they were not tab stops.
    // role="button", not link: they swap the product in the drawer rather than navigating.
    { selector: '.sidebar--color-item:not([href])', role: 'button',
      stateClass: 'active', stateAttr: 'aria-current' },
    // The PDP rails are real links, so they need no role — but the four places that move the
    // selection only toggle `.active`, and aria-current has to follow it.
    { selector: '.sidebar--color-item[href]', stateClass: 'active', stateAttr: 'aria-current' },
    // Icon-font glyphs in an element of their own (bootstrap-select's chevron, FontAwesome/OnerActive <i>s).
    // `hide` skips any that turn out not to be empty.
    { selector: '.bs-caret, .caret, .fa, .oa', hide: true }
  ];

  // Native tab stops. trapTab needs the media and frame entries: a PDP zoom overlay can hold a video
  // whose controls the trap would otherwise skip.
  var FOCUSABLE = 'a[href], button, input, select, textarea, iframe, object, embed, summary, ' +
    'area[href], audio[controls], video[controls], [contenteditable]:not([contenteditable="false"]), ' +
    '[tabindex]:not([tabindex="-1"])';
  var CLONE = '.swiper-slide-duplicate';
  // Bootstrap's own open marker. Watching the class rather than the bs.modal events keeps this
  // file free of jQuery, and catches a modal opened by a plain addClass too.
  var MODAL = '.modal';
  var MODAL_OPEN = 'in';
  // Yotpo's loyalty popups declare aria-modal but trap nothing. Third-party only: the theme's own
  // dialogs are managed where they are built.
  var DIALOG = '.yotpo-loyalty-popup-overlay';
  var DIALOG_CLOSE = '.yotpo-close-button';
  // OneTrust's cookie banner covers the foot of the viewport until consent (342px at phone width), so the
  // footer's tab stops landed under it (2.4.11). Its height feeds the scroll padding in oner_m.
  var COOKIE_BANNER = '#onetrust-banner-sdk';
  var cookieBanner = null;
  // bootstrap-select's wrapper, and the size guide's flex-div tables. Both are named from out
  // here with hidden text, so the class marks what this file added and must not add twice.
  var SELECTPICKER = '.bootstrap-select';
  var WISHLIST_LINK = 'wishlist-link a.wk-button';
  var WISHLIST_TILE = 'wishlist-button-collection button.wk-button';
  var TILE_SUBLINE = '.oa-prdct20-fashion__subline';
  var idCount = 0;
  var SIZE_TABLE = '.fbsg--table';
  var QUICK_ADD_SIZES = '.oa-quick-add__variants';
  // EqualWeb's site script makes each of these a one-tab-stop radio group unless a class contains
  // INDmanualRadio. The theme's templates carry it; Nosto's carousels paint from templates held in Nosto.
  var QUICK_ADD_GROUPS = '.oa-quick-add__colors, .oa-quick-add__variants, .oa-quick-add__variants-size-wrapper';
  var EQUALWEB_SKIP = 'oa-skip-INDmanualRadio';
  var QUICK_ADD_HINT_CLASS = 'oa-quick-add__hint';
  var CAROUSEL = '.swiper-container, .swiper';
  var NAME_CLASS = 'oa-a11y-name';
  // The account forms' server-rendered error summary, and their "we sent you an email" messages.
  var FORM_ERRORS = '[data-a11y="form-errors"], [data-a11y="form-status"]';
  // account-confirmation.liquid: AUTH_START marks logged-out account pages, AUTH_TOAST logged-in ones.
  // The note between them survives the redirect and Sign in with Shop, which bypasses the theme's form.
  var AUTH_START = '[data-a11y="auth-start"]';
  var AUTH_TOAST = '[data-a11y="auth-toast"]';
  var AUTH_KEY = 'oaAuthPending';
  var AUTH_MAX_AGE = 30 * 60 * 1000;
  // WCAG 2.2.2: one page-level toggle (a11y-shortcut-menu.liquid) pauses every autoplay video.
  // HELD marks a video this file paused, so it only resumes what it stopped.
  var MOTION_KEY = 'oaMotionPaused';
  var MOTION_HELD = 'oaMotionHeld';
  // Set only on a video that carried autoplay, so a release puts back exactly what was there.
  var MOTION_AUTOPLAY = 'oaMotionAutoplay';
  // A video's own Play button outranks the page-wide pause for that one video.
  var MOTION_EXEMPT = 'oaMotionExempt';
  var MOTION_TOGGLE = '[data-oa-motion-toggle]';
  // What an untitled <iframe> has to look like before it is named a video player.
  var VIDEO_SRC = /player\.vimeo\.com|youtube(-nocookie)?\.com|\/videos\/|\.(mp4|mov|webm)(\?|$)/i;
  var AUTOPLAY_ON = /([?&])autoplay=1/;
  // Like isMotion(): a muted autoplay is a loop; one with sound was started by the shopper's click.
  var MUTED_EMBED = /[?&](muted|background)=1/;
  var AUTOPLAY_OFF = /([?&])autoplay=0/;
  var FRAME_URL_ATTRS = ['src', 'data-src'];
  // Shopify's CDN serves a GIF's first frame for format=png. ponytail: CDN GIFs only; other hosts
  // and CSS backgrounds keep animating.
  var GIF_URL = /((?:cdn\.shopify\.com|\/cdn\/shop)\/[^\s,?#]+\.gif)(\?[^\s,#]*)?/gi;
  var GIF_STILL = /(\.gif(?:\?[^\s,#]*?)?)[?&]format=png(?=[\s,#]|$)/gi;
  var IMAGE_URL_ATTRS = ['src', 'srcset', 'data-src', 'data-srcset'];
  var STAMP = 'oaA11yStamped';
  // "Already stamped" is this property, not the attribute: cloneNode and Vue's in-DOM templates copy
  // the attribute onto nodes that never got the listener or the state observer.
  var BOUND = 'oaA11yBound';
  var STATE_ONLY = 'state';
  // Swiper's a11y module slides on Enter for these itself, so a click too would move two slides.
  var SWIPER_NAV = '.oa-product-slider__nav-prev, .oa-product-slider__nav-next, ' +
    '.oa-hdr19-bottom__slider__nav-prev, .oa-hdr19-bottom__slider__nav-next';
  var ARROWS = {
    ArrowRight: 1, Right: 1, ArrowDown: 1, Down: 1,
    ArrowLeft: -1, Left: -1, ArrowUp: -1, Up: -1
  };

  var observer = null;
  var inserts = null;
  var liveDialog = null;
  var dialogOpener = null;
  var dialogOpenerLike = null;
  var pendingRoots = [];
  var rescanQueued = false;
  var rescanTimer = null;
  var lastTrigger = null;
  var heldRadio = null;
  var inited = false;
  var motionPaused = false;

  // Every write goes through here: a rescan re-visits elements that are already correct, and on
  // a PLP that is hundreds of no-op setAttribute calls per repaint.
  function setAttr(el, name, value) {
    if (el.getAttribute(name) !== value) {
      el.setAttribute(name, value);
    }
  }

  // Names a control from its own text: the theme's icon-font glyphs and sold-out <svg> land in
  // the computed name otherwise, the glyphs as a private-use character read out as "?".
  function nameFromText(el, group) {
    var text = (el.textContent || '').trim().replace(/\s+/g, ' ');
    if (!text) {
      // Icon-only controls have nothing to name themselves with; group.label speaks for them.
      return;
    }
    var soldOut = window.oaA11yLabels && window.oaA11yLabels.soldOut;
    if (soldOut && group.disabledClass && el.classList.contains(group.disabledClass)) {
      // aria-disabled on its own announces as "dimmed", which is not what it means here.
      text += ' \u2013 ' + soldOut;
    }
    setAttr(el, 'aria-label', text);
  }

  // Names a control from the nearest product title above it. A tile shape this does not know
  // keeps the group's generic name rather than borrowing a neighbour's.
  function nearestMatch(el, selector) {
    var node = el.parentElement;
    for (var hops = 0; node && hops < 5; hops++) {
      if (node.querySelectorAll(TILE_MARK).length > 1) {
        return;
      }
      var found = node.querySelector(selector);
      if (found && (found.textContent || '').trim()) {
        return found;
      }
      node = node.parentElement;
    }
  }

  function nearestText(el, selector) {
    var found = nearestMatch(el, selector);
    return found && found.textContent.trim().replace(/\s+/g, ' ');
  }

  function idFor(el) {
    if (!el.id) {
      el.id = 'oa-a11y-' + (++idCount);
    }
    return el.id;
  }

  function syncState(el, group) {
    if (group.role && !group.label && !group.keepText) {
      nameFromText(el, group);
    }
    if (group.disabledClass) {
      setAttr(el, 'aria-disabled', el.classList.contains(group.disabledClass) ? 'true' : 'false');
    }
    if (group.stateAttr) {
      setAttr(el, group.stateAttr, el.classList.contains(group.stateClass) ? 'true' : 'false');
    }
  }

  // The picker's own title ("Select size") sits just before the list but is wired to nothing,
  // so both groups otherwise announce as a bare "radio group".
  function nameGroup(groupEl) {
    if (groupEl.getAttribute('aria-label') || groupEl.getAttribute('aria-labelledby')) {
      return;
    }
    var prev = groupEl.previousElementSibling;
    for (var hops = 0; prev && hops < 3; hops++) {
      // Another list is another group — its title is not ours to borrow.
      if (prev.tagName === 'UL' || prev.getAttribute('role') === 'radiogroup') {
        return;
      }
      // The two picker titles by name: a substring test on "title" also matches `subtitle`,
      // `title-bar` and anything else that happens to sit above a group.
      var titled = prev.classList.contains('product-sizes-title') ||
        prev.classList.contains('product-logos-title');
      var text = (prev.textContent || '').trim();
      if (titled && text) {
        groupEl.setAttribute('aria-label', text);
        return;
      }
      prev = prev.previousElementSibling;
    }
  }

  function radioItems(groupEl) {
    return Array.prototype.filter.call(groupEl ? groupEl.children : [], function (child) {
      return child.getAttribute('role') === 'radio';
    });
  }

  function stamp(el, group) {
    el[BOUND] = true;
    el.dataset[STAMP] = group.role || STATE_ONLY;

    if (group.hide) {
      if (!(el.textContent || '').trim()) {
        el.setAttribute('aria-hidden', 'true');
      }
      return;
    }

    if (group.role) {
      el.setAttribute('role', group.role);
      // A loop clone is aria-hidden, so nothing inside it may become a tab stop — including
      // controls inserted into a clone that was marked long ago, which hideCloneSlides skips.
      el.setAttribute('tabindex', el.closest(CLONE) ? '-1' : '0');
      // Also delegated from document. addEventListener ignores a second identical binding.
      el.addEventListener('keydown', onControlKey);
      // EqualWeb's scanner reports "no detectable key handler" despite the listener, so the handler
      // is in the markup too. The oaA11yHandled flag keeps it to one run per key.
      if (!el.hasAttribute('onkeydown')) {
        el.setAttribute('onkeydown', 'window.oaA11yControls&&oaA11yControls.key(event)');
      }
    }

    if (group.label && !el.getAttribute('aria-label')) {
      var text = window.oaA11yLabels && window.oaA11yLabels[group.label];
      // labelAttr names the individual control; without it a swatch row is five identical
      // "Colour" buttons.
      var detail = group.labelAttr && el.getAttribute(group.labelAttr);
      if (!detail && group.labelFrom) {
        detail = nearestText(el, group.labelFrom);
      }
      if (text) {
        var name = detail ? text + ' ' + detail : text;
        // Gallery alts are one per-product fallback, so the position tells the views apart. Not data-index:
        // the desktop grid emits 0,1,3,4,5,5 for six images.
        if (group.numberIn) {
          var gallery = el.closest(group.numberIn);
          var peers = gallery && gallery.querySelectorAll(group.selector);
          if (peers && peers.length > 1) {
            name += ' ' + (Array.prototype.indexOf.call(peers, el) + 1);
          }
        }
        el.setAttribute('aria-label', name);
      }
    }

    // role="radio" on a bare <li> contradicts the <ul>'s list semantics unless the list
    // itself becomes the group.
    if (group.group && el.parentElement && el.parentElement.getAttribute('role') !== group.group) {
      el.parentElement.setAttribute('role', group.group);
      nameGroup(el.parentElement);
    }

    if (observer && (group.stateAttr || group.disabledClass)) {
      // Only the state classes matter now — every item is its own tab stop, so nothing has to
      // watch the length switch hide and show them; the browser skips what is display:none.
      observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    }
  }

  // Includes what the groups above stamp tabindex="0" onto — a clone's own quick-add controls.
  function untab(el) {
    var inside = el.querySelectorAll(FOCUSABLE);
    for (var j = 0; j < inside.length; j++) {
      setAttr(inside[j], 'tabindex', '-1');
    }
    if (el.matches(FOCUSABLE)) {
      setAttr(el, 'tabindex', '-1');
    }
  }

  // A clone copies the ids idFor() gave its original, so the labelledby pointing at them became
  // ambiguous. Fresh ids inside the clone, and its own references moved onto them.
  function renumber(clone) {
    var copied = collect(clone, '[id^="oa-a11y-"]', true);
    var map = {};
    for (var i = 0; i < copied.length; i++) {
      var old = copied[i].id;
      copied[i].removeAttribute('id');
      map[old] = idFor(copied[i]);
    }
    var refs = copied.length ? collect(clone, '[aria-labelledby]', true) : [];
    for (var j = 0; j < refs.length; j++) {
      setAttr(refs[j], 'aria-labelledby', refs[j].getAttribute('aria-labelledby').split(/\s+/).map(function (id) {
        return map[id] || id;
      }).join(' '));
    }
  }

  // Swiper's loop clones are off-screen copies, so Tab entered a carousel on a product nobody
  // could see. aria-hidden over a subtree is only honest once nothing in it can be reached.
  function hideCloneSlides(root, scoped) {
    var clones = Array.prototype.slice.call(root.querySelectorAll(CLONE));
    // closest, not matches: oaItemHover fills panels inside an already-marked clone, which the per-clone
    // guard below would skip, leaving tabindex="0" swatches in an aria-hidden subtree.
    var owner = scoped ? root.closest(CLONE) : null;
    if (owner) {
      clones.unshift(owner);
    }
    for (var i = 0; i < clones.length; i++) {
      if (clones[i].dataset.oaA11yClone) {
        continue;
      }
      clones[i].dataset.oaA11yClone = '1';
      clones[i].setAttribute('aria-hidden', 'true');
      clonesToButtons(clones[i]);
      untab(clones[i]);
      renumber(clones[i]);
    }
    if (owner) {
      clonesToButtons(root);
      untab(root);
    }
  }

  // EqualWeb's scanner ignores the clone's aria-hidden and reports each copied wishlist <a> for having
  // no tab stop. A <button> is outside that rule, and Wishlist King mounts by selector, so the copy still works.
  function clonesToButtons(root) {
    var anchors = collect(root, 'a.btn-wishlist.wk-component', true);
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      var button = document.createElement('button');
      for (var k = 0; k < a.attributes.length; k++) {
        // Left stamped, Enter on a mouse-focused copy would click twice: the engine's and the native one.
        if (!/^(role|onkeydown|data-oa-a11y-stamped)$/.test(a.attributes[k].name)) {
          button.setAttribute(a.attributes[k].name, a.attributes[k].value);
        }
      }
      button.setAttribute('type', 'button');
      button.setAttribute('tabindex', '-1');
      while (a.firstChild) {
        button.appendChild(a.firstChild);
      }
      a.parentNode.replaceChild(button, a);
    }
  }

  // Also checks the root (Algolia inserts the tile itself) and ancestors: Vue and bootstrap-select
  // repaint a control's children, so the inserted node sits below the control.
  function collectWithAncestor(root, selector, scoped) {
    var els = collect(root, selector, scoped);
    var up = root.closest && root.closest(selector);
    if (up && els.indexOf(up) === -1) {
      els.push(up);
    }
    return els;
  }

  function collect(root, selector, scoped) {
    var els = Array.prototype.slice.call(root.querySelectorAll(selector));
    if (scoped && root.matches(selector)) {
      els.unshift(root);
    }
    return els;
  }

  // Text only a screen reader gets. A trailing space keeps it off the front of whatever the
  // element already says, which is the value in both callers below.
  function srOnly(text) {
    var span = document.createElement('span');
    span.className = 'sr-only ' + NAME_CLASS;
    span.textContent = text + ' ';
    return span;
  }

  // Visible hint that a tile's size buttons add straight to the cart. aria-hidden: their own labels
  // already say so. CSS reveals it on keyboard focus only.
  function hintQuickAdd(group) {
    var text = window.oaA11yLabels && window.oaA11yLabels.quickAddHint;
    if (!text || group.querySelector('.' + QUICK_ADD_HINT_CLASS)) {
      return;
    }
    var hint = document.createElement('span');
    hint.className = QUICK_ADD_HINT_CLASS;
    hint.setAttribute('aria-hidden', 'true');
    hint.textContent = text;
    group.appendChild(hint);
  }

  // Off-screen carousel slides stay focusable. Removing their tab stops would strand sliders with no
  // arrows, so the carousel follows focus instead, and stops autoplaying under it.
  function followFocus(target) {
    var slide = target && target.closest ? target.closest('.swiper-slide') : null;
    if (!slide) {
      return;
    }
    var container = slide.closest(CAROUSEL);
    var swiper = container && container.swiper;
    if (!swiper) {
      return;
    }
    // Not for a click: stamped divs take focus on mousedown too. oa-pointer is theme.liquid's modality flag.
    if (swiper.autoplay && swiper.autoplay.running && swiper.autoplay.stop &&
        !document.documentElement.classList.contains('oa-pointer')) {
      swiper.autoplay.stop();
    }
    var box = container.getBoundingClientRect();
    var r = slide.getBoundingClientRect();
    var visible = r.right > box.left + 1 && r.left < box.right - 1 &&
      r.bottom > box.top + 1 && r.top < box.bottom - 1;
    if (!box.width || visible) {
      return;
    }
    // slideTo on the DOM position, never slideToLoop: loop mode holds a copy of each slide, and
    // slideToLoop can bring the copy into view instead of the focused node.
    if (swiper.slideTo) {
      swiper.slideTo(Array.prototype.indexOf.call(slide.parentElement.children, slide));
    }
  }

  // bootstrap-select's own button inherits no label from the real select. Hidden text, not aria-label:
  // a label would replace the value in .filter-option rather than precede it.
  function nameSelectpicker(el) {
    var button = el.querySelector('button.dropdown-toggle');
    if (!button || button.querySelector('.' + NAME_CLASS)) {
      return;
    }
    var select = el.querySelector('select');
    var name = select ? (select.getAttribute('aria-label') || '').trim() : '';
    if (!name) {
      var label = el.previousElementSibling;
      name = label && label.tagName === 'LABEL' ? (label.textContent || '').trim() : '';
    }
    // An untouched picker may read "Size Size". Deliberate: the observer never sees a size being picked,
    // so a label skipped here would never arrive, and a value with no label is worse.
    if (name) {
      // Before .filter-option, which bootstrap-select rewrites in place on every change.
      button.insertBefore(srOnly(name), button.firstChild);
    }
  }

  // WCAG 2.5.3: the header link's aria-label omits its visible count, so the name moves onto the icon.
  // The label is a constant in the app's template, so a count change does not put it back.
  function nameWishlistLink(link) {
    var icon = link.querySelector('wk-icon');
    var name = link.getAttribute('aria-label');
    if (!icon || !name) {
      return;
    }
    icon.setAttribute('role', 'img');
    icon.setAttribute('aria-label', name);
    link.removeAttribute('aria-label');
  }

  // Wishlist King's tile buttons all read "Add to Wishlist". labelledby rather than a label: the app
  // rewrites aria-label when the product is added, and referencing the button itself keeps that live.
  function nameWishlistTile(button) {
    var title = !button.hasAttribute('aria-labelledby') && nearestMatch(button, TILE_TITLE);
    if (!title) {
      return;
    }
    var ids = [idFor(button), idFor(title)];
    // The colour line, when there is one — the grid shows each colour of a product as its own tile.
    var subline = title.parentElement.querySelector(TILE_SUBLINE);
    if (subline && (subline.textContent || '').trim()) {
      ids.push(idFor(subline));
    }
    button.setAttribute('aria-labelledby', ids.join(' '));
  }

  // The size guide is flex divs, so each value gets its column as hidden text. ARIA table roles do not
  // fit: the cm and inch sub-rows nest inside the size row.
  function labelSizeChart(table) {
    var heads = table.querySelectorAll('.fbsg--headerrow .fbsg-headercell');
    var rows = table.querySelectorAll('.fbsg--subrow');
    for (var r = 0; r < rows.length; r++) {
      var cells = rows[r].children;
      for (var c = 0; c < cells.length; c++) {
        // Aligned from the right, since only some tables lead with a unit cell. A cell landing on column 0
        // (the row's size heading) is that unit cell and keeps its own text.
        var head = heads[heads.length - (cells.length - c)];
        if (!head || heads.length - (cells.length - c) < 1 ||
            cells[c].querySelector('.' + NAME_CLASS)) {
          continue;
        }
        var text = (head.textContent || '').trim();
        if (text) {
          cells[c].insertBefore(srOnly(text), cells[c].firstChild);
        }
      }
    }
  }

  // The LS sections, carousels and tiles start their loops with play() and no autoplay
  // attribute, so muted without controls is what marks a video as motion.
  function isMotion(el) {
    return el.tagName === 'VIDEO' && (el.autoplay || (el.muted && !el.controls));
  }

  // autoplay is removed rather than only paused: a source swap or a re-attach restarts a video
  // that still carries it.
  function holdVideo(video) {
    if (video.dataset[MOTION_HELD] || video.dataset[MOTION_EXEMPT]) {
      return;
    }
    // 'stopped': it had played and something else paused it — a section's own toggle, or a
    // carousel parking an inactive slide. Resuming must leave those as they were.
    video.dataset[MOTION_HELD] = video.paused && video.played && video.played.length ? 'stopped' : '1';
    if (video.autoplay) {
      video.dataset[MOTION_AUTOPLAY] = '1';
      video.autoplay = false;
    }
    video.pause();
  }

  // True when the video was running at the time it was held.
  function unholdVideo(video) {
    var running = video.dataset[MOTION_HELD] === '1';
    delete video.dataset[MOTION_HELD];
    if (video.dataset[MOTION_AUTOPLAY]) {
      delete video.dataset[MOTION_AUTOPLAY];
      video.autoplay = true;
    }
    return running;
  }

  function releaseVideo(video) {
    if (!unholdVideo(video)) {
      return;
    }
    var playing = video.play();
    // Chrome rejects play() on a video that is off-screen or not yet buffered. It autoplays on
    // its own once it is, now that the attribute is back.
    if (playing && playing.catch) {
      playing.catch(function () {});
    }
  }

  // Embeds autoplay from a url flag and cannot be paused from out here without the provider's API,
  // so a held one reloads with autoplay=0. A loaded frame is replaced: navigating it adds history.
  function swapFrameUrl(frame, from, to) {
    var target = frame.getAttribute('src') ? frame.cloneNode(false) : frame;
    for (var i = 0; i < FRAME_URL_ATTRS.length; i++) {
      var url = target.getAttribute(FRAME_URL_ATTRS[i]);
      if (url) {
        target.setAttribute(FRAME_URL_ATTRS[i], url.replace(from, to));
      }
    }
    if (target !== frame && frame.parentNode) {
      frame.parentNode.replaceChild(target, frame);
    }
    return target;
  }

  function holdFrame(frame) {
    var urls = (frame.getAttribute('src') || '') + ' ' + (frame.getAttribute('data-src') || '');
    if (!frame.dataset[MOTION_HELD] && VIDEO_SRC.test(urls) && AUTOPLAY_ON.test(urls) && MUTED_EMBED.test(urls)) {
      swapFrameUrl(frame, AUTOPLAY_ON, '$1autoplay=0').dataset[MOTION_HELD] = '1';
    }
  }

  function releaseFrame(frame) {
    // Before the swap, or the copy carries the flag.
    delete frame.dataset[MOTION_HELD];
    swapFrameUrl(frame, AUTOPLAY_OFF, '$1autoplay=1');
  }

  // Same element, same classes, so a still GIF keeps its layout.
  function holdGif(el) {
    if (el.dataset[MOTION_HELD]) {
      return;
    }
    for (var i = 0; i < IMAGE_URL_ATTRS.length; i++) {
      var value = el.getAttribute(IMAGE_URL_ATTRS[i]);
      var still = value && value.replace(GIF_URL, function (url, file, query) {
        return query && /[?&]format=/.test(query) ? url : file + (query ? query + '&' : '?') + 'format=png';
      });
      if (still && still !== value) {
        el.setAttribute(IMAGE_URL_ATTRS[i], still);
        el.dataset[MOTION_HELD] = '1';
      }
    }
  }

  // Every attribute, not only the ones held: LazyLoad may have promoted a held data-src since.
  function releaseGif(el) {
    delete el.dataset[MOTION_HELD];
    for (var i = 0; i < IMAGE_URL_ATTRS.length; i++) {
      var value = el.getAttribute(IMAGE_URL_ATTRS[i]);
      var animated = value && value.replace(GIF_STILL, '$1');
      if (animated && animated !== value) {
        el.setAttribute(IMAGE_URL_ATTRS[i], animated);
      }
    }
  }

  function syncMotionToggles() {
    var toggles = document.querySelectorAll(MOTION_TOGGLE);
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-pressed', motionPaused ? 'true' : 'false');
    }
  }

  function setMotionPaused(paused) {
    motionPaused = !!paused;
    try {
      window.localStorage.setItem(MOTION_KEY, motionPaused ? '1' : '0');
    } catch (error) {
      // Private mode. The toggle still holds for this page.
    }
    // A new page-wide choice outranks whatever a single video's own button said before it.
    var exempt = document.querySelectorAll('video[data-oa-motion-exempt]');
    for (var e = 0; e < exempt.length; e++) {
      delete exempt[e].dataset[MOTION_EXEMPT];
    }
    if (motionPaused) {
      scan(document);
    } else {
      var held = document.querySelectorAll('video[data-oa-motion-held]');
      for (var i = 0; i < held.length; i++) {
        releaseVideo(held[i]);
      }
      var embeds = document.querySelectorAll('iframe[data-oa-motion-held]');
      for (var em = 0; em < embeds.length; em++) {
        releaseFrame(embeds[em]);
      }
      var gifs = document.querySelectorAll('img[data-oa-motion-held], source[data-oa-motion-held]');
      for (var gi = 0; gi < gifs.length; gi++) {
        releaseGif(gifs[gi]);
      }
    }
    syncMotionToggles();
    announceMotion();
    return motionPaused;
  }

  // For players that keep their own paused flag and label: the LS sections listen for this.
  function announceMotion() {
    document.dispatchEvent(new CustomEvent('oa:motionchange', { detail: { paused: motionPaused } }));
  }

  function scan(root) {
    root = root || document;
    var scoped = root.nodeType === 1;
    if (motionPaused) {
      var vids = collect(root, 'video', scoped);
      for (var v = 0; v < vids.length; v++) {
        // A paused loop with no autoplay is left out: nothing started it, so nothing may resume it.
        if (vids[v].autoplay || (!vids[v].paused && isMotion(vids[v]))) {
          holdVideo(vids[v]);
        }
      }
      var embeds = collect(root, 'iframe', scoped);
      for (var em = 0; em < embeds.length; em++) {
        holdFrame(embeds[em]);
      }
      var gifs = collect(root, 'img, source', scoped);
      for (var gi = 0; gi < gifs.length; gi++) {
        holdGif(gifs[gi]);
      }
    }
    for (var g = 0; g < GROUPS.length; g++) {
      var group = GROUPS[g];
      var els = collect(root, GROUPS[g].selector, scoped);
      for (var i = 0; i < els.length; i++) {
        if (!els[i][BOUND]) {
          stamp(els[i], group);
        }
        syncState(els[i], group);
      }
    }
    // After the groups, so the tabindex="0" they stamp inside a clone is overwritten, not the
    // other way round.
    hideCloneSlides(root, scoped);

    var modals = collect(root, MODAL, scoped);
    for (var m = 0; m < modals.length; m++) {
      watchModal(modals[m]);
    }
    var banner = collect(root, COOKIE_BANNER, scoped)[0];
    if (banner) {
      watchCookieBanner(banner);
    }
    var pickers = collect(root, SELECTPICKER, scoped);
    for (var p = 0; p < pickers.length; p++) {
      nameSelectpicker(pickers[p]);
    }
    // bootstrap-select 1.11 puts role="combobox" on its popup without the aria-expanded and
    // aria-controls that role requires; the list inside is already the listbox.
    var comboboxes = collect(root, SELECTPICKER + ' [role="combobox"]', scoped);
    for (var cb = 0; cb < comboboxes.length; cb++) {
      comboboxes[cb].removeAttribute('role');
    }
    var wishlists = collect(root, WISHLIST_LINK, scoped);
    for (var w = 0; w < wishlists.length; w++) {
      nameWishlistLink(wishlists[w]);
    }
    var tileWishlists = collect(root, WISHLIST_TILE, scoped);
    for (var tw = 0; tw < tileWishlists.length; tw++) {
      nameWishlistTile(tileWishlists[tw]);
    }
    var tables = collect(root, SIZE_TABLE, scoped);
    for (var t = 0; t < tables.length; t++) {
      labelSizeChart(tables[t]);
    }
    var quickAddSizes = collectWithAncestor(root, QUICK_ADD_SIZES, scoped);
    for (var qa = 0; qa < quickAddSizes.length; qa++) {
      hintQuickAdd(quickAddSizes[qa]);
    }
    // The campaign articles' Vimeo embeds have no title, so a frame list reads as blanks. Only
    // video frames get the generic name; a chat widget or consent banner must not.
    var frames = collect(root, 'iframe:not([title])', scoped);
    var frameName = window.oaA11yLabels && window.oaA11yLabels.videoPlayer;
    for (var f = 0; frameName && f < frames.length; f++) {
      // data-src too: product-video-lazy.liquid and the hero banner hold the url there until
      // LazyLoad promotes it, and an attribute change is not something the insert observer sees.
      if (VIDEO_SRC.test(frames[f].getAttribute('src') || frames[f].getAttribute('data-src') || '')) {
        frames[f].setAttribute('title', frameName);
      }
    }
  }

  // An account form that fails validation re-renders server-side with its summary at the top and
  // focus back at the start of the page, so nothing reports that the submit failed.
  function focusFormErrors() {
    var els = document.querySelectorAll(FORM_ERRORS);
    for (var i = 0; i < els.length; i++) {
      if (els[i].offsetParent !== null && (els[i].textContent || '').trim()) {
        els[i].focus();
        return;
      }
    }
  }

  // Shows the login / account-created popup once, on the first logged-in page after the account
  // page that left the note. It never takes focus; the text is read out through oaAnnounce.
  function authConfirmation() {
    var start = document.querySelector(AUTH_START);
    var toast = document.querySelector(AUTH_TOAST);
    var note = null;
    try {
      // Shopify adds checkout_url to the login form itself, and that login lands back in checkout.
      if (start && !document.querySelector('input[name="checkout_url"]')) {
        window.sessionStorage.setItem(AUTH_KEY, JSON.stringify({ kind: start.dataset.kind, at: Date.now() }));
      } else if (start || toast) {
        // Only an account page or a logged-in one clears it: the bot challenge sits in between.
        note = JSON.parse(window.sessionStorage.getItem(AUTH_KEY));
        window.sessionStorage.removeItem(AUTH_KEY);
      }
    } catch (error) {
      note = null;
    }
    var text = toast && note && Date.now() - note.at < AUTH_MAX_AGE && toast.getAttribute('data-' + note.kind);
    if (!text) {
      return;
    }
    var close = function () {
      var hadFocus = toast.contains(document.activeElement);
      toast.hidden = true;
      if (hadFocus && document.getElementById('MainContent')) {
        document.getElementById('MainContent').focus();
      }
    };
    toast.querySelector('.oa-auth-toast__text').textContent = text;
    toast.querySelector('.oa-auth-toast__close').addEventListener('click', close);
    // The bar's CSS countdown is the timer, so pausing it on hover and focus pauses the close too.
    toast.querySelector('.oa-auth-toast__bar').addEventListener('animationend', close);
    toast.hidden = false;
    // A beat after load, or the screen reader starting on the new page talks over it.
    window.setTimeout(function () {
      if (window.oaAnnounce) {
        window.oaAnnounce(text);
      }
    }, 1000);
  }

  function groupFor(el) {
    for (var i = 0; i < GROUPS.length; i++) {
      if (el.matches(GROUPS[i].selector)) {
        return GROUPS[i];
      }
    }
    return null;
  }

  function onMutation(records) {
    for (var i = 0; i < records.length; i++) {
      var el = records[i].target;
      if (el.dataset.oaA11yModal) {
        syncModal(el);
        continue;
      }
      var group = groupFor(el);
      if (!group) {
        continue;
      }
      syncState(el, group);
    }
  }

  // The one focusable query in the theme; header.js.liquid reads it off window.oaA11yControls.
  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll(FOCUSABLE),
      function (el) {
        // Any negative tabindex is out of the tab order, not just -1: bootstrap-select's tabindex="-98"
        // select passes the size check below.
        var tabindex = el.getAttribute('tabindex');
        if (tabindex !== null && parseInt(tabindex, 10) < 0) {
          return false;
        }
        // el.disabled covers every disableable element at once, and is undefined on the rest.
        // Focusing a disabled control is a no-op, and trapTab has already eaten the Tab by then.
        return !el.disabled && (el.offsetWidth > 0 || el.offsetHeight > 0);
      }
    );
  }

  // Shared by both drawers. A Tab-cycling trap, not `inert` on the rest of the page — inert
  // needs a polyfill in the browsers this theme still supports.
  function trapTab(panel, event) {
    if (!panel) {
      return;
    }
    var visible = focusables(panel);
    if (!visible.length) {
      return;
    }
    var first = visible[0];
    var last = visible[visible.length - 1];
    // -1 is the panel itself, which both drawers focus on open and which the query above
    // filters out as tabindex="-1" — without it the first Shift+Tab walks out of the trap.
    var at = visible.indexOf(document.activeElement);
    if (event.shiftKey && (at === 0 || at === -1)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (at === visible.length - 1 || at === -1)) {
      event.preventDefault();
      first.focus();
    }
  }

  // Hiding ends in display:none, so the observed height drops to 0 once consent is given.
  function watchCookieBanner(banner) {
    if (banner === cookieBanner || !window.ResizeObserver) {
      return;
    }
    cookieBanner = banner;
    new ResizeObserver(function () {
      document.documentElement.style.setProperty('--oa-cookie-banner', banner.offsetHeight + 'px');
    }).observe(banner);
  }

  function drain() {
    if (!rescanQueued) {
      return;
    }
    rescanQueued = false;
    // Whichever of the two triggers ran first, the other must not survive to drain the next
    // batch early — on an infinite scroll that doubles the scans.
    if (rescanTimer) {
      clearTimeout(rescanTimer);
      rescanTimer = null;
    }
    // A batch often reports a subtree and nodes inside it too; scan() is 24 sweeps, so skip covered roots.
    var roots = new Set(pendingRoots);
    pendingRoots = [];
    // syncDialog() forces layout, so only when the overlay can have come or gone.
    var dialogChanged = !!liveDialog && !dialogVisible(liveDialog);
    roots.forEach(function (root) {
      if (!root.isConnected) {
        return;
      }
      for (var up = root.parentElement; up; up = up.parentElement) {
        if (roots.has(up)) {
          return;
        }
      }
      if (!dialogChanged && (root.matches(DIALOG) || root.querySelector(DIALOG))) {
        dialogChanged = true;
      }
      scan(root);
    });
    if (dialogChanged) {
      syncDialog();
    }
  }

  // Synchronous, not in the rescan: EqualWeb builds its widgets from timers, which run after this callback.
  function skipEqualWebRadios(root) {
    var groups = collect(root, QUICK_ADD_GROUPS, root.nodeType === 1);
    for (var i = 0; i < groups.length; i++) {
      groups[i].classList.add(EQUALWEB_SKIP);
    }
  }

  // Rescans only inserted subtrees, coalesced to one pass; a document-wide sweep per insertion is too
  // slow under infinite scroll. Stamping touches attributes, so it cannot loop.
  function onInsert(records) {
    // A dialog closing is a removal and nothing else, so it would never reach drain() on the
    // added-node count alone.
    var removed = false;
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType === 1) {
          skipEqualWebRadios(added[j]);
          pendingRoots.push(added[j]);
        }
      }
      if (liveDialog && records[i].removedNodes.length) {
        removed = true;
      }
    }
    if ((!pendingRoots.length && !removed) || rescanQueued) {
      return;
    }
    rescanQueued = true;
    // rAF does not fire in a background tab, which would pin pendingRoots forever; the timer
    // is the fallback, and whichever runs first clears the flag for the other.
    requestAnimationFrame(drain);
    rescanTimer = setTimeout(drain, 100);
  }

  // Safari focuses nothing on a button click or tap, so activeElement is <body> there. The click is
  // the record that survives, and it fires for Enter and Space too.
  function onClickCapture(event) {
    var el = event.target && event.target.closest && event.target.closest(FOCUSABLE);
    if (el) {
      lastTrigger = el;
    }
  }

  // The element a drawer should hand focus back to when it closes.
  function opener() {
    var el = document.activeElement;
    if (!el || el === document.body || el === document.documentElement) {
      return lastTrigger;
    }
    return el;
  }

  // Bootstrap 3 drops focus on <body> when a modal closes; hand it back to the opener here rather
  // than in every snippet that opens a .modal.
  function watchModal(el) {
    if (el.dataset.oaA11yModal) {
      return;
    }
    el.dataset.oaA11yModal = '1';
    // BS3 traps focus behind a backdrop but never says so.
    if (!el.hasAttribute('aria-modal')) {
      el.setAttribute('aria-modal', 'true');
    }
    el.dataset.oaA11yOpen = el.classList.contains(MODAL_OPEN) ? '1' : '';
    if (observer) {
      observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function syncModal(el) {
    var open = el.classList.contains(MODAL_OPEN);
    if (!!el.dataset.oaA11yOpen === open) {
      return;
    }
    el.dataset.oaA11yOpen = open ? '1' : '';
    if (open) {
      // A pop-up on a timer was not opened by anyone, so there is nothing to hand focus back to.
      if (navigator.userActivation && !navigator.userActivation.isActive) {
        el.oaLastFocused = null;
        return;
      }
      // Bootstrap may already have moved focus inside; then the click record is the opener.
      var from = opener();
      el.oaLastFocused = from && !el.contains(from) ? from : lastTrigger;
      return;
    }
    var last = el.oaLastFocused;
    el.oaLastFocused = null;
    // A modal opened from a tile that Algolia has since repainted has nothing left to go back to.
    if (last && document.contains(last)) {
      last.focus({ preventScroll: true });
    }
  }

  function dialogVisible(el) {
    return !!el && el.isConnected && (el.offsetWidth > 0 || el.offsetHeight > 0);
  }

  // Yotpo re-renders its widget while the rewards history is open, detaching the opener. What it
  // looked like is enough to find the node that replaced it.
  function describe(el) {
    if (!el || typeof el.className !== 'string') {
      return null;
    }
    return { tag: el.tagName, cls: el.className,
      name: (el.getAttribute('aria-label') || el.textContent || '').trim() };
  }

  function findLike(like) {
    if (!like) {
      return null;
    }
    var els = document.getElementsByTagName(like.tag);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.className === like.cls && (el.offsetWidth > 0 || el.offsetHeight > 0) &&
          (el.getAttribute('aria-label') || el.textContent || '').trim() === like.name) {
        return el;
      }
    }
    return null;
  }

  // Yotpo mounts and unmounts the overlay rather than toggling it, so there is no state to
  // read: whichever one is on screen now is the open dialog.
  function syncDialog() {
    var el = document.querySelector(DIALOG);
    if (!dialogVisible(el)) {
      el = null;
    }
    if (el === liveDialog) {
      return;
    }
    if (liveDialog && !el) {
      var last = dialogOpener;
      if (last && !document.contains(last)) {
        last = findLike(dialogOpenerLike);
      }
      liveDialog = null;
      dialogOpener = null;
      dialogOpenerLike = null;
      // Focus is on the close button Yotpo has just unmounted, so it falls to <body> and the
      // shopper restarts at the top of the account page.
      if (last) {
        last.focus();
      }
      return;
    }
    liveDialog = el;
    if (el) {
      // Yotpo focuses its own title inside the overlay before this runs, so the focused element
      // is not what opened it — the click record is, as in watchModal.
      var from = opener();
      dialogOpener = from && !el.contains(from) ? from : lastTrigger;
      dialogOpenerLike = describe(dialogOpener);
      // Nothing inside is focused on open, so Tab would carry on from the page behind.
      setAttr(el, 'tabindex', '-1');
      el.focus();
    }
  }

  function onKeydown(event) {
    // Insertion is how the overlay normally arrives; adopting it on the first Tab covers a
    // Yotpo build that only toggles it instead.
    if (!liveDialog && event.key === 'Tab') {
      syncDialog();
    }
    if (dialogVisible(liveDialog)) {
      if (event.key === 'Tab') {
        trapTab(liveDialog, event);
      } else if (event.key === 'Escape') {
        var close = liveDialog.querySelector(DIALOG_CLOSE);
        if (close) {
          event.preventDefault();
          close.click();
        }
      }
    }
    onControlKey(event);
  }

  // Runs from the control's own listener and again from document's, so it acts once per event.
  // The document copy covers nodes that carry the stamp without the listener, like a loop clone.
  function onControlKey(event) {
    if (event.oaA11yHandled) {
      return;
    }
    event.oaA11yHandled = true;
    var el = event.target;
    if (!el || !el.dataset || !el.dataset[STAMP] || el.dataset[STAMP] === STATE_ONLY) {
      return;
    }

    // Alt+Left is Back, Ctrl/Cmd+Enter opens in a new tab: a chorded key is the browser's.
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    var step = ARROWS[event.key];
    if (step && el.dataset[STAMP] === 'radio') {
      var items = radioItems(el.parentElement).filter(function (item) {
        return item.offsetParent !== null;
      });
      var at = items.indexOf(el);
      if (at === -1) {
        return;
      }
      // Wrapping is what the radio pattern specifies: the group is a cycle, not a list.
      var next = items[(at + step + items.length) % items.length];
      event.preventDefault();
      next.focus();
      // A held arrow only moves focus, and onKeyup picks where it stops: each pick swaps the
      // gallery, and a sweep through every size must not fire one per size.
      heldRadio = event.repeat ? next : null;
      // Not a sold-out size either: its click opens the back-in-stock drawer, whose trap strands
      // the arrows. Enter still opens it.
      if (!event.repeat && next.getAttribute('aria-disabled') !== 'true') {
        next.click();
      }
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
      return;
    }
    // Holding Enter would otherwise re-open the quick-add sidebar on every repeat. Arrows above
    // keep theirs: repeating a step through a radio group is what the key is for.
    if (event.repeat) {
      event.preventDefault();
      return;
    }
    // Space scrolls the page and Enter submits the surrounding form; neither is wanted here.
    event.preventDefault();
    if (event.key === 'Enter' && el.matches(SWIPER_NAV)) {
      return;
    }
    el.click();
  }

  function onKeyup(event) {
    var el = heldRadio;
    heldRadio = null;
    if (el && el === event.target && el.getAttribute('aria-checked') !== 'true' &&
        el.getAttribute('aria-disabled') !== 'true') {
      el.click();
    }
  }

  // A tile's quick-add panel is built on the first mouseover, so replay it on focus — whichever handler
  // is bound (inline on Algolia tiles, Vue's @mouseover elsewhere) then builds it.
  function onFocusIn(event) {
    var el = event.target;
    var tile = el && el.closest && el.closest('.oa-prdct20-fashion__image-container');
    if (!tile || tile.dataset.oaA11yHovered) {
      return;
    }
    tile.dataset.oaA11yHovered = '1';
    tile.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
  }

  function init() {
    if (inited) {
      return;
    }
    inited = true;

    if (window.MutationObserver) {
      observer = new MutationObserver(onMutation);
      inserts = new MutationObserver(onInsert);
      inserts.observe(document.body, { childList: true, subtree: true });
    }
    skipEqualWebRadios(document);
    // Before the scan, which is what actually stops the videos it finds.
    var stored = null;
    try {
      stored = window.localStorage.getItem(MOTION_KEY);
    } catch (error) {
      stored = null;
    }
    // The OS preference is only the default. A choice made in the shortcut menu outranks it,
    // in both directions — someone who asked for motion back gets it.
    motionPaused = stored === null
      ? !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
      : stored === '1';
    syncMotionToggles();

    scan(document);
    // Players that started before this deferred file ran still show "Pause".
    if (motionPaused) {
      announceMotion();
    }
    syncDialog();
    focusFormErrors();
    authConfirmation();
    document.addEventListener('focusin', function (event) { followFocus(event.target); });
    // Carousels, tiles and sections start videos themselves, so pausing once is not enough.
    // play does not bubble but does capture, which holds them all from here.
    document.addEventListener('play', function (event) {
      var el = event.target;
      if (!motionPaused || !el || !el.dataset || el.dataset[MOTION_EXEMPT]) {
        return;
      }
      if (el.dataset[MOTION_HELD]) {
        el.pause();
      } else if (isMotion(el)) {
        holdVideo(el);
      }
    }, true);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('keyup', onKeyup);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('click', onClickCapture, true);
    // No oa:colorswitch listener: the swap* functions rewrite innerHTML, which the insert
    // observer above already sees as added nodes.
  }

  window.oaA11yControls = {
    scan: scan, init: init, trapTab: trapTab, focusables: focusables, opener: opener, key: onControlKey,
    // a11y-shortcut-menu.liquid drives these; it is inline and parses before this deferred
    // file, so it may only call them from a handler.
    motion: {
      paused: function () { return motionPaused; },
      toggle: function () { return setMotionPaused(!motionPaused); },
      // Call from a video's own Play button, before play(): that video runs while the rest stay
      // held, until the page-wide toggle is next used.
      release: function (video) {
        if (motionPaused && video && video.dataset) {
          video.dataset[MOTION_EXEMPT] = '1';
          unholdVideo(video);
        }
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
